import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { apiFetch } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ message: '', kind: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ message: '', kind: '' });
    try {
      const res = await apiFetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
        silent: true,
      });
      const data = await res.clone().json().catch(() => ({}));
      if (res.ok) {
        setStatus({
          message: data.message || 'If an account exists for that email, we\'ve sent a reset link.',
          kind: 'success',
        });
        setEmail('');
      } else {
        setStatus({ message: data.detail || 'Something went wrong. Please try again.', kind: 'error' });
      }
    } catch {
      setStatus({ message: 'Network error. Please try again.', kind: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="grid-background absolute inset-0 opacity-20"></div>
      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/login"
          className="flex items-center space-x-2 text-[#00f0ff] hover:text-[#ff00ff] mb-6 transition-colors"
          data-testid="forgot-back-link"
        >
          <ArrowLeft size={20} />
          <span>Back to sign in</span>
        </Link>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-[#00f0ff] via-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text mb-2">
            Forgot Password?
          </h1>
          <p className="text-[#00f0ff]/70">Enter your email and we&apos;ll send you a reset link.</p>
        </div>
        <div className="glass rounded-2xl p-8 glow-box">
          {status.message && (
            <div
              className={`mb-6 p-4 rounded-lg text-sm ${
                status.kind === 'success'
                  ? 'bg-[#00ff00]/10 border border-[#00ff00]/40 text-[#00ff00]'
                  : 'bg-red-500/20 border border-red-500 text-red-400'
              }`}
              data-testid="forgot-status"
            >
              {status.message}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00f0ff]/50" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none"
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 btn-primary text-white font-bold rounded-lg disabled:opacity-50"
              data-testid="forgot-submit-button"
            >
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
